import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import './BesperAi.css';

function dqlApiBase() {
  const env = typeof window !== 'undefined' ? window._env_ : {};
  const explicit = env.DQL_DEMO_API || env.REACT_APP_DQL_DEMO_API;
  if (explicit && explicit !== 'same-origin') {
    return String(explicit).replace(/\/$/, '');
  }
  return '';
}

async function api(path, options = {}) {
  const base = dqlApiBase();
  const url = base ? `${base}${path}` : path;
  const res = await fetch(url, {
    headers: { 'Content-Type': 'application/json', ...(options.headers || {}) },
    ...options,
  });
  const data = await res.json();
  if (!res.ok) {
    throw new Error(data.error || res.statusText);
  }
  return data;
}

const DQL_FORMS = [
  {
    form: 'fact',
    example: 'fact late_delivery(c1).',
    note: 'Ground atoms — extend the session EDB before materialisation.',
  },
  {
    form: 'norm',
    example: 'norm r1: late_delivery(?C) => must(supplier, pay_late_fee(?C)).',
    note: 'Defeasible rules with priorities — preloaded per scenario (see Norms panel).',
  },
  {
    form: 'ask',
    example: 'ask must(supplier, pay_late_fee(c1))?',
    note: 'Engine proves or returns inconclusive; ships a proof chain when direct.',
  },
  {
    form: 'argue',
    example: 'argue must_not(jones, tell(them)).',
    note: 'Proof-carrying claim — verifier replay yields RLVR +1 / 0 / −1 (Stage E).',
  },
];

const GUIDED_PROGRAMS = {
  late_delivery: `# Late delivery — fee owed
fact contract(c1).
fact delivery_due(c1, day10).
fact delivered(c1, day15).
fact late_delivery(c1).

ask must(supplier, pay_late_fee(c1))?
argue must(supplier, pay_late_fee(c1)).`,
  late_delivery_fm: `# Force majeure — higher-priority norm blocks fee
fact contract(c1).
fact late_delivery(c1).
fact force_majeure_declared(c1).

ask must_not(supplier, pay_late_fee(c1))?
argue must_not(supplier, pay_late_fee(c1)).`,
  chisholm_ctd: `# Chisholm contrary-to-duty — no SDL paradox
ask must(jones, help(them))?
ask must_not(jones, tell(them))?
argue must_not(jones, tell(them)).`,
  preference: `# Preference canary — add a fact then query
fact likes(me, orange_1).

ask likes(me, orange_1)?
ask not_likes(me, apple_4)?`,
};

const BesperAi = () => {
  const [scenarios, setScenarios] = useState([]);
  const [scenarioId, setScenarioId] = useState('late_delivery');
  const [session, setSession] = useState(null);
  const [facts, setFacts] = useState([]);
  const [rules, setRules] = useState([]);
  const [program, setProgram] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [health, setHealth] = useState(null);

  const selected = useMemo(
    () => scenarios.find((s) => s.id === scenarioId),
    [scenarios, scenarioId],
  );

  const refreshSessionData = useCallback(async (sid) => {
    const [f, r] = await Promise.all([
      api(`/api/dql/sessions/${sid}/facts`),
      api(`/api/dql/sessions/${sid}/rules`),
    ]);
    setFacts(f.facts || []);
    setRules(r.rules || []);
  }, []);

  const startSession = useCallback(async (id) => {
    setLoading(true);
    setError('');
    setResults([]);
    try {
      const created = await api('/api/dql/sessions', {
        method: 'POST',
        body: JSON.stringify({ scenario_id: id }),
      });
      setSession(created);
      const sample = scenarios.find((s) => s.id === id);
      const lines = [
        ...(sample?.sample_facts || []),
        '',
        ...(sample?.sample_asks || []),
        ...(sample?.sample_argues?.length ? ['', ...sample.sample_argues] : []),
      ];
      setProgram(lines.join('\n'));
      await refreshSessionData(created.session_id);
    } catch (e) {
      setError(e.message);
      setSession(null);
    } finally {
      setLoading(false);
    }
  }, [refreshSessionData, scenarios]);

  useEffect(() => {
    api('/api/dql/health')
      .then(setHealth)
      .catch((e) => setHealth({ status: 'offline', error: e.message }));
    api('/api/dql/scenarios')
      .then((d) => setScenarios(d.scenarios || []))
      .catch((e) => setError(e.message));
  }, []);

  useEffect(() => {
    if (scenarios.length && !session) {
      startSession(scenarioId);
    }
  }, [scenarios, scenarioId, session, startSession]);

  const runProgram = async () => {
    if (!session?.session_id) return;
    setLoading(true);
    setError('');
    try {
      const out = await api(`/api/dql/sessions/${session.session_id}/run`, {
        method: 'POST',
        body: JSON.stringify({ program }),
      });
      setResults(out.results || []);
      setSession(out.session || session);
      await refreshSessionData(session.session_id);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  const appendLine = (line) => {
    setProgram((p) => (p.trim() ? `${p.trim()}\n${line}` : line));
  };

  const loadGuided = () => {
    const guided = GUIDED_PROGRAMS[scenarioId];
    if (guided) {
      setProgram(guided);
      setResults([]);
    }
  };

  return (
    <div className="besper-ai-page">
      <header className="besper-ai-header">
        <div>
          <p className="besper-ai-kicker">Besper AI · DLS demo</p>
          <h1>Deontic Query Language — live engine</h1>
          <p className="besper-ai-lead">
            Compose <code>fact</code>, <code>ask</code>, and <code>argue</code> against prepared
            datasets. Norms are preloaded per scenario (see <em>Norms</em> below). The engine
            returns verdicts with proof chains; <code>argue</code> simulates Reasoner RLVR
            (+1 verified, 0 unprovable, −1 refuted) per{' '}
            <Link to="/besper-ai-lab" className="besper-ai-link">Besper AI Lab</Link>.
          </p>
        </div>
        <div className="besper-ai-header-meta">
          <span className={`besper-ai-pill ${health?.status === 'ok' ? 'ok' : 'warn'}`}>
            Engine {health?.status === 'ok' ? 'online' : 'offline'}
          </span>
          <Link to="/" className="besper-ai-link">← Platform home</Link>
        </div>
      </header>

      {error && (
        <div className="besper-ai-error" role="alert">
          {error}
            {health?.status !== 'ok' && (
            <p className="besper-ai-error-hint">
              Start the demo API:{' '}
              <code>python -m dag3_pilot.v09.demo.server --port 8770</code>
              {' '}(proxied at <code>/api/dql</code> via gateway or dev server)
            </p>
          )}
        </div>
      )}

      <div className="besper-ai-grid">
        <section className="besper-ai-panel">
          <h2>Scenario</h2>
          <select
            value={scenarioId}
            onChange={(e) => {
              setScenarioId(e.target.value);
              setSession(null);
            }}
            disabled={loading}
          >
            {scenarios.map((s) => (
              <option key={s.id} value={s.id}>{s.title}</option>
            ))}
          </select>
          {selected && <p className="besper-ai-desc">{selected.description}</p>}
          <button
            type="button"
            className="besper-ai-btn"
            disabled={loading}
            onClick={() => startSession(scenarioId)}
          >
            Reload scenario
          </button>
          {GUIDED_PROGRAMS[scenarioId] && (
            <button
              type="button"
              className="besper-ai-btn"
              disabled={loading}
              onClick={loadGuided}
            >
              Load guided example
            </button>
          )}
          {session && (
            <dl className="besper-ai-stats">
              <div><dt>Facts</dt><dd>{session.fact_count}</dd></div>
              <div><dt>Derived</dt><dd>{session.derived_count}</dd></div>
              <div><dt>Rules</dt><dd>{session.rule_count}</dd></div>
            </dl>
          )}
          <h3>Quick insert</h3>
          <div className="besper-ai-chips">
            {(selected?.sample_facts || []).map((line) => (
              <button key={line} type="button" className="besper-ai-chip" onClick={() => appendLine(line)}>
                {line}
              </button>
            ))}
            {(selected?.sample_asks || []).map((line) => (
              <button key={line} type="button" className="besper-ai-chip ask" onClick={() => appendLine(line)}>
                {line}
              </button>
            ))}
            {(selected?.sample_argues || []).map((line) => (
              <button key={line} type="button" className="besper-ai-chip argue" onClick={() => appendLine(line)}>
                {line}
              </button>
            ))}
          </div>
        </section>

        <section className="besper-ai-panel besper-ai-editor-panel">
          <h2>DQL program</h2>
          <textarea
            className="besper-ai-editor"
            value={program}
            onChange={(e) => setProgram(e.target.value)}
            rows={14}
            spellCheck={false}
            placeholder={'fact contract(c1).\nask must(supplier, pay_late_fee(c1))?'}
          />
          <button
            type="button"
            className="besper-ai-btn primary"
            disabled={loading || !session}
            onClick={runProgram}
          >
            {loading ? 'Running…' : 'Run against engine'}
          </button>
        </section>

        <section className="besper-ai-panel besper-ai-results-panel">
          <h2>Results</h2>
          {results.length === 0 && <p className="besper-ai-muted">Run a program to see answers.</p>}
          {results.map((r, i) => (
            <article key={`${r.statement}-${i}`} className="besper-ai-result">
              <header>
                <span className="besper-ai-result-kind">{r.kind || 'ask'}</span>
                <code>{r.statement}</code>
              </header>
              {r.status && (
                <p>
                  <strong>Status:</strong> {r.status}
                  {r.verified != null && (
                    <span className={r.verified ? 'ok-text' : 'bad-text'}>
                      {' '}· verified: {String(r.verified)}
                    </span>
                  )}
                </p>
              )}
              {r.verdict && <p><strong>Verdict:</strong> {r.verdict}</p>}
              {r.reward != null && (
                <p>
                  <strong>RLVR:</strong> {r.rlvr_label} ({r.reward > 0 ? '+' : ''}{r.reward})
                </p>
              )}
              {r.proof_hash && (
                <p className="besper-ai-hash"><strong>Proof:</strong> {r.proof_hash.slice(0, 24)}…</p>
              )}
              {r.chain_steps?.length > 0 && (
                <ol className="besper-ai-chain">
                  {r.chain_steps.map((s) => (
                    <li key={s.step_id}>
                      Step {s.step_id}: rule <code>{s.rule_id}</code>
                      {Object.keys(s.bindings || {}).length > 0 && (
                        <span> · {JSON.stringify(s.bindings)}</span>
                      )}
                    </li>
                  ))}
                </ol>
              )}
              {r.rendered && <pre className="besper-ai-rendered">{r.rendered}</pre>}
            </article>
          ))}
        </section>
      </div>

      <div className="besper-ai-grid besper-ai-grid-secondary">
        <section className="besper-ai-panel">
          <h2>Facts in dataset</h2>
          <ul className="besper-ai-list">
            {facts.map((f) => (
              <li key={f.hash + f.dql}>
                <code>{f.dql}</code>
                <span className="besper-ai-tag">{f.source}</span>
              </li>
            ))}
          </ul>
        </section>
        <section className="besper-ai-panel">
          <h2>Norms (rules)</h2>
          <ul className="besper-ai-list">
            {rules.map((r) => (
              <li key={r}><code>{r}</code></li>
            ))}
          </ul>
        </section>
      </div>

      <section className="besper-ai-panel besper-ai-forms-ref">
        <h2>DQL four sentence forms</h2>
        <p className="besper-ai-muted">
          Aligned with the DLS foundations report — the engine implements all four; this demo lets
          you author <code>fact</code>, <code>ask</code>, and <code>argue</code> while norms ship
          with each scenario.
        </p>
        <div className="besper-ai-forms-grid">
          {DQL_FORMS.map((row) => (
            <article key={row.form} className="besper-ai-form-card">
              <h3>{row.form}</h3>
              <code>{row.example}</code>
              <p>{row.note}</p>
              {row.form !== 'norm' && (
                <button
                  type="button"
                  className="besper-ai-chip"
                  onClick={() => appendLine(row.example)}
                >
                  Insert example
                </button>
              )}
            </article>
          ))}
        </div>
      </section>
    </div>
  );
};

export default BesperAi;
